import { GlobalSettingsKeys } from '../../../api/models/GlobalSettingsKeys';
import { getTime } from '../../utility/DateFormatter';
import { Registry, Router } from '@lightningjs/sdk';
import { StreamInfoType } from '../streamInfo/StreamInfo';
import { Backend } from '../../../api/Backend';
import { AutoplayScreen } from '../../../pages/detail/AutoplayScreen';
import { AutoplayHelper } from '../AutoplayHelper';

/**
 * @enum {string}
 */
export const OverlayState = {
  CONTROLS: 'Controls',
  HIDDEN: 'Hidden',
  CHANNELBAR: 'ChannelBar',
  MINI_EPG: 'MiniEpg',
  TRICK_PLAY: 'TrickPlay',
  TITLE: 'Title',
  ERROR: 'Error',
};

export const Buttons = {
  PLAY: 'Play',
  PAUSE: 'Pause',
  REWIND: 'Rewind',
  FORWARD: 'Forward',
  SETTINGS: 'Settings',
};

export class OverlayHelper {
  private _backend: Backend;
  private _overlayState: string;
  private _overlayStateHandler: ((state: string, params?: any) => void) | undefined;
  private _showTimer: number | undefined;

  constructor(backend: Backend) {
    this._backend = backend;
    this._overlayState = OverlayState.HIDDEN;
  }

  set overlayStateHandler(handler: (state: string) => void) {
    this._overlayStateHandler = handler;
  }

  onStart() {
    if (this._backend.playerController.streamManager.streamType === StreamInfoType.LINEAR) {
      this._overlayState = OverlayState.TITLE;
    } else {
      // if replay/ startover / vod needs to start with controls
      if (this._backend.playerController.seekEnabled) {
        this._overlayState = OverlayState.CONTROLS;
      } else {
        this._overlayState = OverlayState.TITLE;
      }
    }
    if (this._overlayStateHandler) this._overlayStateHandler(this._overlayState);
    this._startShowTimer();
  }

  handleUp() {
    if (this._overlayState === OverlayState.ERROR) {
      return;
    }
    if (this._overlayState === OverlayState.HIDDEN || this._overlayState === OverlayState.TITLE) {
      if (this._backend.playerController.streamManager.streamType === StreamInfoType.LINEAR) {
        this._overlayState = OverlayState.MINI_EPG;
        if (this._overlayStateHandler) this._overlayStateHandler(this._overlayState);
      } else {
        this._overlayState = OverlayState.CONTROLS;
        if (this._overlayStateHandler) this._overlayStateHandler(this._overlayState);
      }
    }
    this._startShowTimer();
  }

  handleLeft() {
    if (this._overlayState === OverlayState.ERROR) {
      return;
    }
    if (this._backend.playerController.streamManager.streamType === StreamInfoType.LINEAR) {
      if (this._overlayState === OverlayState.HIDDEN || this._overlayState === OverlayState.TITLE) {
        this._overlayState = OverlayState.CHANNELBAR;
        if (this._overlayStateHandler) this._overlayStateHandler(this._overlayState);
      } else if (this._overlayState === OverlayState.CHANNELBAR) {
        this._overlayState = OverlayState.HIDDEN;
        if (this._overlayStateHandler) this._overlayStateHandler(this._overlayState);
      }
    } else if (this._overlayState === OverlayState.HIDDEN || this._overlayState === OverlayState.TITLE) {
      this._backend.playerController.controlsManager.handleSeekEvent(false);
      this._overlayState = OverlayState.CONTROLS;
      if (this._overlayStateHandler) this._overlayStateHandler(this._overlayState, { focusSeekbar: true });
    }
    this._startShowTimer();
  }

  handleDown() {
    if (this._overlayState === OverlayState.ERROR) {
      return;
    }
    if (this._overlayState === OverlayState.HIDDEN || this._overlayState === OverlayState.TITLE) {
      this._overlayState = OverlayState.CONTROLS;
      if (this._overlayStateHandler) this._overlayStateHandler(this._overlayState);
    }
    this._startShowTimer();
  }

  handleRight() {
    if (this._overlayState === OverlayState.ERROR) {
      return;
    }
    if (this._backend.playerController.streamManager.streamType === StreamInfoType.LINEAR) {
      if (this._overlayState === OverlayState.HIDDEN || this._overlayState === OverlayState.TITLE) {
        this._overlayState = OverlayState.CHANNELBAR;
        if (this._overlayStateHandler) this._overlayStateHandler(this._overlayState);
      } else if (this._overlayState === OverlayState.CHANNELBAR) {
        this._overlayState = OverlayState.HIDDEN;
        if (this._overlayStateHandler) this._overlayStateHandler(this._overlayState);
      }
    } else if (this._overlayState === OverlayState.HIDDEN || this._overlayState === OverlayState.TITLE) {
      this._backend.playerController.controlsManager.handleSeekEvent(true);
      this._overlayState = OverlayState.CONTROLS;
      if (this._overlayStateHandler) this._overlayStateHandler(this._overlayState, { focusSeekbar: true });
    }
    this._startShowTimer();
  }

  handleBack() {
    if (this._showTimer) {
      this.hideAll();
    } else if (
      this._overlayState !== OverlayState.TRICK_PLAY &&
      this._backend.playerController.streamInfo?.streamType !== StreamInfoType.LINEAR
    ) {
      if (this._overlayState !== OverlayState.ERROR) {
        this._backend.playerController.endStream();
      }
      const showAutoPlay = AutoplayHelper.autoplayEnabled; // playercontroller, can show autoplay
      const nextAsset = this._backend.playerController.nextAsset;
      if (showAutoPlay && nextAsset) {
        this._backend.playerController.endStream();
        const currentAmount = this._backend.playerController.autoplayCurrentCounter;
        const maxAmount = AutoplayHelper.autoplayMaxAssetsAutoStart;
        Router.navigate('main', {
          autoplayDialog: {
            page: AutoplayScreen,
            params: {
              backend: this._backend,
              previousAsset: this._backend.playerController.streamInfo,
              data: nextAsset,
              enableCountDown: currentAmount < maxAmount,
            },
          },
        });
      } else {
        this._backend.playerController.fallbackToLinearStream();
        Router.navigate('main');
      }
    } else {
      return true;
    }
  }

  handleStop() {
    // linear cant press stop
    if (this._backend.playerController.streamInfo?.streamType !== StreamInfoType.LINEAR) {
      if (this._overlayState !== OverlayState.ERROR) {
        this._backend.playerController.endStream();
      }
      this._backend.playerController.fallbackToLinearStream();
      Router.navigate('main');
    }
  }

  handleEnter() {
    if (this._overlayState === OverlayState.ERROR) {
      Router.navigate('main');
    } else if (this._showTimer && this._overlayState !== OverlayState.TITLE) {
      this._startShowTimer();
    } else if (this._backend.playerController.streamManager.streamType === StreamInfoType.LINEAR) {
      Router.navigate('main');
    } else if (this._overlayState !== OverlayState.TRICK_PLAY) {
      this.handlePlayPause();
    }
  }

  handlePlayPause() {
    if (this._overlayState === OverlayState.ERROR) {
      return;
    }
    this._overlayState = OverlayState.CONTROLS;
    if (this._overlayStateHandler) this._overlayStateHandler(this._overlayState);
    this._backend.playerController.controlsManager.togglePlayPause();
    this._startShowTimer();
  }

  handlePlay() {
    if (this._overlayState === OverlayState.ERROR) {
      return;
    }
    this._overlayState = OverlayState.CONTROLS;
    if (this._overlayStateHandler) this._overlayStateHandler(this._overlayState);
    this._backend.playerController.controlsManager.handleActivateButton(Buttons.PLAY);
    this._startShowTimer();
  }

  handlePause() {
    if (this._overlayState === OverlayState.ERROR) {
      return;
    }
    this._overlayState = OverlayState.CONTROLS;
    if (this._overlayStateHandler) this._overlayStateHandler(this._overlayState);
    this._backend.playerController.controlsManager.handleActivateButton(Buttons.PAUSE);
    this._startShowTimer();
  }

  handleRewind() {
    if (this._overlayState === OverlayState.ERROR) {
      return;
    }
    this._backend.playerController.controlsManager.handleFastRewind();
  }

  handleForward() {
    if (this._overlayState === OverlayState.ERROR) {
      return;
    }
    if (this._backend.playerController.streamManager.streamType === StreamInfoType.LINEAR) {
      return;
    }
    this._backend.playerController.controlsManager.handleFastForward();
  }

  handleChannelUp() {
    if (this._overlayState === OverlayState.ERROR) {
      return;
    }
    if (
      this._overlayState === OverlayState.HIDDEN ||
      this._overlayState === OverlayState.TITLE ||
      this._overlayState === OverlayState.CONTROLS
    ) {
      this._overlayState = OverlayState.CHANNELBAR;
      if (this._overlayStateHandler) this._overlayStateHandler(this._overlayState, { channelUp: true });
    }
    // this._backend.playerController.channelUp();
    this._startShowTimer();
  }

  handleChannelDown() {
    if (this._overlayState === OverlayState.ERROR) {
      return;
    }
    if (
      this._overlayState === OverlayState.HIDDEN ||
      this._overlayState === OverlayState.TITLE ||
      this._overlayState === OverlayState.CONTROLS
    ) {
      this._overlayState = OverlayState.CHANNELBAR;
      if (this._overlayStateHandler) this._overlayStateHandler(this._overlayState, { channelDown: true });
    }
    // this._backend.playerController.channelDown();
    this._startShowTimer();
  }

  handleError() {
    this._clearShowTimer();
    this._overlayState = OverlayState.ERROR;
    if (this._overlayStateHandler) this._overlayStateHandler(this._overlayState);
  }

  /**
   * @param {boolean} trickPlay
   */
  setTrickPlay(trickPlay: boolean) {
    if (trickPlay) {
      this._clearShowTimer();
      this._overlayState = OverlayState.TRICK_PLAY;
      if (this._overlayStateHandler) this._overlayStateHandler(this._overlayState);
    } else {
      this._overlayState = OverlayState.CONTROLS;
      if (this._overlayStateHandler) this._overlayStateHandler(this._overlayState);
      this._startShowTimer();
    }
  }

  _getHideTimeout(): number {
    return this._backend.globalSettings.numberValue(GlobalSettingsKeys.playerHideControlsPeriod) || 5000;
  }

  /**
   * @private
   */
  _startShowTimer() {
    if (this._overlayState !== OverlayState.TRICK_PLAY) {
      this._clearShowTimer();
      this._showTimer = Registry.setTimeout(() => {
        this._showTimer = undefined;
        this.hideAll();
      }, this._getHideTimeout());
    }
  }

  startShowTimer() {
    this._startShowTimer();
  }

  hideAll() {
    this._overlayState = OverlayState.HIDDEN;
    if (this._overlayStateHandler) this._overlayStateHandler(this._overlayState);
    this._clearShowTimer();
    this._showTimer = undefined;
  }

  /**
   * @private
   */
  _clearShowTimer() {
    if (this._showTimer) {
      Registry.clearTimeout(this._showTimer);
      this._showTimer = undefined;
    }
  }

  stopShowTimer() {
    this._clearShowTimer();
  }

  /**
   * @returns {string}
   */
  static getClock() {
    const now = new Date();
    return getTime(now);
  }
}
